import { useTranslation } from 'react-i18next'
import { Clock } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatConversationDateTime } from '@/lib/dateFormat'
import { ChannelIcons } from './ChannelIcons'
import { OutcomeBadge } from './OutcomeBadge'
import { formatDurationCompact } from './DurationDisplay'
import { BackendVersionBadge } from './BackendVersionBadge'
import { getBackendVersionKind } from '@/lib/backendVersion'
import type { Conversation } from '@/types/conversation'

interface ConversationListItemProps {
  conversation: Conversation
  assistantName?: string
  assistantTimezone?: string
  onClick?: () => void
  isViewed?: boolean
  isAlternate?: boolean
  /** Backend image tag; pass only for admins. Non-release tags get a colored bar and a label. */
  backendVersion?: string | null
  className?: string
}

/**
 * Mobile-friendly list item for displaying conversation information.
 * Shows essential info in a compact, scannable format.
 */
export function ConversationListItem({
  conversation,
  assistantName,
  assistantTimezone,
  onClick,
  isViewed = false,
  isAlternate = false,
  backendVersion,
  className,
}: ConversationListItemProps) {
  const { t } = useTranslation()
  const versionKind = getBackendVersionKind(backendVersion)

  return (
    <div
      onClick={onClick}
      className={cn(
        'flex cursor-pointer flex-col gap-1.5 border-b border-gray-100 px-4 py-4 transition-colors active:bg-gray-100 dark:border-gray-800 dark:active:bg-gray-700',
        isAlternate
          ? 'bg-gray-50 dark:bg-gray-900'
          : 'bg-white dark:bg-gray-950',
        versionKind === 'rc' && 'shadow-[inset_4px_0_0_0_var(--color-amber-500)]',
        versionKind === 'preview' && 'shadow-[inset_4px_0_0_0_var(--color-purple-500)]',
        versionKind === 'smoke' && 'shadow-[inset_4px_0_0_0_var(--color-blue-500)]',
        isViewed && 'opacity-70',
        className
      )}
    >
      {/* Top row: Date + Duration | Icons */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {conversation.updated_at
              ? formatConversationDateTime(conversation.updated_at, assistantTimezone)
              : t('resources.conversations.unknown')}
          </span>
          {conversation.duration && conversation.duration > 0 && (
            <span className="flex items-center gap-1 text-xs text-gray-500">
              <Clock className="h-3 w-3" />
              {formatDurationCompact(conversation.duration)}
            </span>
          )}
          {versionKind && <BackendVersionBadge version={backendVersion} />}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <ChannelIcons meta={conversation.meta} twilioId={conversation.twilio_id} />
          {conversation.outcome && (
            <OutcomeBadge outcome={conversation.outcome} />
          )}
        </div>
      </div>

      {/* Contact info: phone number + name on same line */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {conversation.from_id || t('resources.conversations.unknown')}
        </span>
        {conversation.from_name && (
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {conversation.from_name}
          </span>
        )}
      </div>

      {/* Assistant + Topic on same line */}
      {(assistantName || conversation.topic) && (
        <div className="flex items-center gap-2">
          {assistantName && (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {assistantName}
            </span>
          )}
          {assistantName && conversation.topic && (
            <span className="text-gray-300 dark:text-gray-600">•</span>
          )}
          {conversation.topic && (
            <span className="text-sm font-medium text-brand-600 dark:text-brand-400">
              {conversation.topic}
            </span>
          )}
        </div>
      )}

      {/* Summary - full width */}
      {conversation.summary && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {conversation.summary}
        </p>
      )}
    </div>
  )
}

export default ConversationListItem
